import type { PaginatedMeta } from "@/types/formList";
import type { FormSubmissionDto } from "@/types/submission";

function submissionTimestamp(submission: FormSubmissionDto): number {
  const raw = submission.createdAt ?? submission.created_at;
  if (!raw) return 0;
  const time = new Date(raw).getTime();
  return Number.isNaN(time) ? 0 : time;
}

export function sortSubmissionsNewestFirst(
  items: FormSubmissionDto[],
): FormSubmissionDto[] {
  return [...items].sort(
    (a, b) => submissionTimestamp(b) - submissionTimestamp(a),
  );
}

export function buildClientPaginationMeta(
  total: number,
  page: number,
  perPage: number,
): PaginatedMeta {
  const lastPage = Math.max(1, Math.ceil(total / perPage));
  const safePage = Math.min(Math.max(page, 1), lastPage);
  const from = total === 0 ? null : (safePage - 1) * perPage + 1;
  const to = total === 0 ? null : Math.min(safePage * perPage, total);

  return {
    current_page: safePage,
    last_page: lastPage,
    per_page: perPage,
    total,
    from,
    to,
  };
}

export function paginateSubmissions(
  items: FormSubmissionDto[],
  page: number,
  perPage: number,
): {
  pageItems: FormSubmissionDto[];
  sorted: FormSubmissionDto[];
  meta: PaginatedMeta;
} {
  const sorted = sortSubmissionsNewestFirst(items);
  const meta = buildClientPaginationMeta(sorted.length, page, perPage);
  const start = (meta.current_page - 1) * perPage;

  return {
    pageItems: sorted.slice(start, start + perPage),
    sorted,
    meta,
  };
}
